// Google Analytics 4 — gtag loader, pageview + event tracker for the React SPA.
// Ported from daowatches_frontend (src/core/analytics/ga4.js), extended so GA
// can answer "who uses what, in which store":
//   - setUserContext(): GA user_id + user properties (team/customer, role, plan,
//     store) — and the same store/role on EVERY event, so a hit is filed under
//     the store that was active when it happened, not the user's home store.
//   - trackPageview(): page_view with the route template and feature name.
//   - trackEvent(): login, store_switch, api_rate_limited, api_error.
// Only ids, roles and store/plan names are sent — never a person's name, email
// or phone (GA terms forbid PII).
//
// Why a custom wrapper instead of dropping <script> into index.html:
// - SPAs don't fire navigation events, so route changes must be tracked manually
//   via gtag('event', 'page_view'). See GA4PageTracker for the route hook.
// - Vite env vars need a JS module to be read; index.html can't access import.meta.env.
//
// GA admin setup this code relies on (see the PR): register the custom
// dimensions below, and turn OFF Enhanced measurement → "Page changes based on
// browser history events" or every route change is counted twice.

import axios from "axios";

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
// Vite sets import.meta.env.PROD = true whenever the bundle is built in
// production mode (i.e. `vite build`, which is what Jenkins runs for both
// staging and production deploys).
const IS_PROD_BUILD = import.meta.env.PROD;

// GA truncates user-property values at 36 characters; do it ourselves so the
// cut is predictable.
const MAX_PROPERTY = 36;

let initialized = false;
// Event-scoped context merged into every hit (store_id, store_name, rc_role).
let eventContext = {};

export function isGA4Enabled() {
  if (!MEASUREMENT_ID) return false;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "localhost") return false;
  // Staging builds also run `vite build` and so read .env.production — keep
  // test traffic out of the production property by hostname instead.
  if (host.startsWith("staging")) return false;
  return true;
}

/** /api/listings/123?x=1 -> /api/listings/:id — ids collapsed, query dropped. */
export function routeTemplate(path) {
  return String(path || "")
    .split("?")[0]
    .split("/")
    .map((s) => (/^(\d+|[0-9a-f]{8}-[0-9a-f-]{27,}|[0-9a-f]{24,})$/i.test(s) ? ":id" : s))
    .join("/");
}

const trim = (v) => (v == null || v === "" ? null : String(v).slice(0, MAX_PROPERTY));

export function initGA4() {
  if (initialized || !isGA4Enabled()) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag("js", new Date());
  // send_page_view: false — we fire page_view manually on every route change
  // (including the initial load) from GA4PageTracker, so GA's auto pageview
  // would double-count.
  gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    debug_mode: !IS_PROD_BUILD,
  });

  // Rate limiting and server errors, as the user experienced them. Pass-through:
  // the rejection continues to every other handler unchanged.
  axios.interceptors.response.use(undefined, (error) => {
    const status = error?.response?.status;
    if (status === 429 || status >= 500) {
      trackEvent(status === 429 ? "api_rate_limited" : "api_error", {
        api_endpoint: routeTemplate(error?.config?.url || ""),
        api_method: String(error?.config?.method || "get").toUpperCase(),
        api_status: status,
      });
    }
    return Promise.reject(error);
  });
}

export function trackEvent(name, params = {}) {
  if (!isGA4Enabled() || !window.gtag) return;
  window.gtag("event", name, { ...eventContext, ...params });
}

export function trackPageview(path, { title, feature } = {}) {
  trackEvent("page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title || document.title,
    page_template: routeTemplate(path),
    rc_feature: feature || null,
  });
}

/**
 * Tag every following hit with the signed-in user and the store they are
 * working in. Pass null on sign-out.
 *
 * @param {{id, isTeam, storeId, storeName, role, plan}|null} ctx
 *   storeId/storeName = the ACTIVE store (store switcher), not the home store;
 *   storeId null for a master admin on the unscoped Master store.
 */
export function setUserContext(ctx) {
  if (!isGA4Enabled() || !window.gtag) return;

  const store = ctx ? (ctx.storeId ? String(ctx.storeId) : "master") : null;
  eventContext = ctx
    ? { store_id: store, store_name: trim(ctx.storeName), rc_role: ctx.role || null }
    : {};

  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    user_id: ctx?.id ? String(ctx.id) : null,
  });
  window.gtag("set", "user_properties", {
    rc_team: ctx ? (ctx.isTeam ? "team" : "customer") : null,
    rc_store_id: store,
    rc_store_name: ctx ? trim(ctx.storeName) : null,
    rc_role: ctx?.role || null,
    rc_plan: ctx?.plan || null,
  });
}
